import './quick-add.scss'
import ModalDialog from '../global/custom-elements/modal-dialog';

export default class QuickAddModal extends ModalDialog {
    /**
     * Class constructor
     *
     * @arg {object} - `el` Selector for the media gallery
    */
    constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddModalContent"]');
    }

    hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        setTimeout(() => {
            this.modalContent.innerHTML = '';
        }, 300);

        if (preventFocus) this.openedBy = null;
        super.hide();
    }

    show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading-overlay__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
            .then((response) => response.text())
            .then((responseText) => {
                const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
                this.productElement = responseHTML.querySelector('[id^="MainProduct-"]');
                this.preventDuplicatedIDs();
                this.removeDOMElements();
                this.adjustDOMElements();
                this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

                if (window.Shopify && window.Shopify.PaymentButton) {
                    window.Shopify.PaymentButton.init();
                }

                if (window.ProductModel) window.ProductModel.loadShopifyXR();

                this.removeGalleryListSemantic();
                this.preventVariantURLSwitching();
                super.show(opener);
            })
            .finally(() => {
                opener.removeAttribute('aria-disabled');
                opener.classList.remove('loading');
                opener.querySelector('.loading-overlay__spinner').classList.add('hidden');
            });
    }

    setInnerHTML(element, html) {
        element.innerHTML = html;

        // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
        element.querySelectorAll('script').forEach(oldScriptTag => {
            const newScriptTag = document.createElement('script');
            Array.from(oldScriptTag.attributes).forEach(attribute => {
                newScriptTag.setAttribute(attribute.name, attribute.value)
            });
            newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
            oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
    }

    preventVariantURLSwitching() {
        const variantPicker = this.modalContent.querySelector('variant-radios,variant-selects');
        if (!variantPicker) return;
        variantPicker.setAttribute('data-update-url', 'false');
    }

    removeDOMElements() {
        const deleteDomElements = [
            'nav',
            'pickup-availability',
            'product-modal',
            '.swiper',
            '.swiper-scrollbar',
            '.js-product-accordion',
            '.js-quick-add-context-product-media',
            '.js-quick-add-context-wysiwyg',
            '.js-quick-add-context-free-delivery-banner',
            '.js-quick-add-context-wtb-ctas',
            '.js-product-kf-images',
            '.js-product-kf-icons',
            '.js-quick-add-context-heading-graphic',
            '.js-pdp-cta-sticky'
        ];

        deleteDomElements.forEach(selector => {
            const elements = this.productElement.querySelectorAll(selector);
            if (elements.length > 0) {
                elements.forEach(element => element.remove());
            }
        });

        this.keepOnlyQuickAddEssentials();
    }

    keepOnlyQuickAddEssentials() {
        const productInfo = this.productElement.querySelector('[id^="ProductInfo-"]');
        if (!productInfo) return;

        const keepSelectors = [
            '.js-quick-add-context-product-type',
            '.pdp-quick-add__heading-container',
            '.js-quick-add-context-price',
            '.js-quick-add-context-variants',
            '.js-quick-add-context-quantity-atc'
        ];

        Array.from(productInfo.children).forEach(child => {
            const shouldKeep = keepSelectors.some(sel => child.matches(sel) || child.querySelector(sel));
            if (!shouldKeep) child.remove();
        });
    }

    adjustDOMElements() {
        const wrap = this.productElement.querySelector('.pdp-quick-add, .js-quick-add-context-wrap');
        if (wrap) {
            wrap.classList.add('mediumMax:wrap', 'medium:px-10');
            wrap.classList.remove('medium:wrap');
        }

        const container = this.productElement.querySelector('.pdp-quick-add__container, .js-quick-add-context-container');
        if (container) {
            container.classList.add('py-12');
            container.classList.remove('container', 'medium:container', 'medium:mx-auto');
        }

        const productGrid = this.productElement.querySelector('.pdp-quick-add__grid, .js-quick-add-context-grid');
        if (productGrid) productGrid.classList.remove('grid', 'grid-cols-4', 'medium:grid-cols-12', 'medium:gap-x-5');

        const modalMoreInfo = this.productElement.querySelector('.js-quick-add-more-info-cta, .product__modal-more-info');
        if (modalMoreInfo) {
            modalMoreInfo.classList.remove('hidden');
            modalMoreInfo.classList.add('inline-block');
        }

        const productCarousel = this.productElement.querySelector('.js-pdp-media-gallery');
        if (productCarousel) productCarousel.classList.remove('js-pdp-media-gallery');

        const productInfoContainer = this.productElement.querySelector('.pdp-quick-add__info-container, .js-quick-add-context-product-info-container');
        if (productInfoContainer) {
            productInfoContainer.classList.remove('col-span-4', 'mediumMax:mt-10', 'mediumMax:wrap', 'medium:col-span-6', 'xLarge:col-span-5', 'xLarge:col-start-8');
        }

        const h1 = this.productElement.querySelector('.js-quick-add-context-heading');
        if (h1) {
            h1.classList.add('heading-xs', 'mr-20', 'medium:mr-24');
        }

        const priceSwap = this.productElement.querySelector('.js-quick-add-context-price');
        if (priceSwap) {
            priceSwap.classList.remove('mt-5');
            priceSwap.classList.add('mt-[0.625rem]', 'medium:mt-5');
        }

        const variants = this.productElement.querySelector('.js-quick-add-context-variants');
        if (variants) {
            variants.classList.remove('mt-5', 'mt-[1.875rem]', 'xLarge:mt-6', 'xLarge:mt-8');
            variants.classList.add('medium:mt-[1.5625rem]');
        }

        const quantityAtc = this.productElement.querySelector('.js-quick-add-context-quantity-atc');
        if (quantityAtc) {
            quantityAtc.classList.remove('items-center', 'gap-3', 'gap-4', 'gap-7', 'mt-9', 'xLarge:mt-10');
            quantityAtc.classList.add('flex-col', 'gap-10', 'medium:gap-[4.5rem]');
        }

        const quantityInput = this.productElement.querySelector('.js-quick-add-context-quantity-input');
        if (quantityInput) {
            quantityInput.classList.add('w-full');
            const input = quantityInput.querySelector('.quantity-input');
            if (input) {
                input.classList.add('quantity--mini');
            }
        }

        const productForm = this.productElement.querySelector('.js-quick-add-context-product-form');
        if (productForm) {
            productForm.classList.remove('w-full');
            productForm.classList.add('inline-block', 'w-auto');
        }

        const submit = this.productElement.querySelector('.js-quick-add-context-submit');
        if (submit) {
            submit.classList.remove('button--full-width-mobile', 'mediumMax:[.product_&]:button--full-width-mobile');
        }
    }

    preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;
        this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${ sectionId }`);
        this.productElement.querySelectorAll('variant-selects, variant-radios').forEach((variantSelect) => {
            variantSelect.dataset.originalSection = sectionId;
        });
    }

    removeGalleryListSemantic() {
        const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute('role', 'presentation'));
    }
}
